/** Pure presentation helpers for the "active on phone" screens (phase 5). */
import type { DeviceActivity, DeviceCount, DevicePoint } from "@apphq/shared/types";
import { dayShort } from "./format";

const SOURCE_LABELS: Readonly<Record<string, string>> = {
  app: "App geopend",
  widget: "Widget",
  background: "Achtergrond",
  notification: "Melding",
  other: "Overig",
  unknown: "Onbekend",
};

export function sourceLabel(key: string | null | undefined): string {
  if (!key) return "Onbekend";
  return SOURCE_LABELS[key] ?? key;
}

/** Version-like split keys: "other"/"unknown" get a readable label, real versions pass through. */
export function versionLabel(key: string): string {
  if (key === "other") return "Overig";
  if (key === "unknown" || key === "") return "Onbekend";
  return key;
}

export interface DeviceSummary {
  active: number;
  opened: number;
  passive: number;
  /** Share of the active devices whose app was really opened, 0-100 (0 without devices). */
  openedPercent: number;
}

/** Splits the 7-day active devices into opened vs widget/background-only; clamps so the parts never exceed the whole. */
export function summarizeDevices(t: Pick<DeviceActivity, "active_7d" | "opened_7d">): DeviceSummary {
  const active = Math.max(0, t.active_7d);
  const opened = Math.min(active, Math.max(0, t.opened_7d));
  const passive = active - opened;
  return { active, opened, passive, openedPercent: active > 0 ? Math.round((opened / active) * 100) : 0 };
}

export interface DeviceChartPoint {
  date: string;
  label: string;
  active: number;
  opened: number;
}

/** Series -> chart rows: sorted by day, de-duplicated (last wins), `opened` never above `active`. */
export function toDeviceChartPoints(points: readonly DevicePoint[]): DeviceChartPoint[] {
  const byDay = new Map<string, DevicePoint>();
  for (const p of points) byDay.set(p.date, p);
  return [...byDay.values()]
    .sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : 0))
    .map((p) => ({ date: p.date, label: dayShort(p.date), active: p.active, opened: Math.min(p.opened, p.active) }));
}

/** Bar rows for a split (zero rows dropped). */
export function splitRows(rows: readonly DeviceCount[], label: (key: string) => string): Array<{ key: string; label: string; value: number }> {
  return rows.filter((r) => r.count > 0).map((r) => ({ key: r.key, label: label(r.key), value: r.count }));
}
